// ComunicacaoTemplates.jsx
import { useEffect, useState, useContext, useRef } from "react";
import { Button } from "@mui/material";
import { TextField } from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { AuthContext } from "../../../shared/context/auth-context";
import { Popconfirm } from "antd";
import ErrorIcon from "@mui/icons-material/Error";
import EmailIcon from "@mui/icons-material/Email";
import toast from "react-hot-toast";
import { useLoading } from "../../../shared/context/LoadingContext";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NotaPartilhaUnidades from "../components/NotaPartilhaUnidades";

const VARIAVEIS_EMAIL = [
  { chave: "nome_cliente", label: "Nome do Cliente" },
  { chave: "nome_clinica", label: "Nome da Clínica" },
  { chave: "contacto_clinica", label: "Contacto da Clínica" },
  { chave: "data_hoje", label: "Data de Hoje" },
];

const VARIAVEIS_SMS = [
  { chave: "nome_cliente", label: "Nome do Cliente" },
  { chave: "nome_clinica", label: "Nome da Clínica" },
  { chave: "contacto_clinica", label: "Contacto da Clínica" },
  { chave: "data", label: "Data do Agendamento" },
  { chave: "hora", label: "Hora do Agendamento" },
  { chave: "dia_semana", label: "Dia da Semana" },
  { chave: "medico", label: "Nome do Médico" },
  { chave: "link_confirmacao", label: "Link de Confirmação de Presença" },
];

const TIPOS_TEMPLATE_EMAIL = [
  { value: "orcamentoPorAprovar", label: "Orçamento" },
  { value: "aniversario", label: "Aniversário" },
  { value: "marketing", label: "Marketing" },
  { value: "geral", label: "Geral" },
];

const TIPOS_TEMPLATE_SMS = [
  { value: "lembrete", label: "Lembrete de agendamento" },
  { value: "confirmacao", label: "Confirmação de agendamento" },
];

function ComunicacaoTemplates({ sendRequest, clinicaId }) {
  const { startLoading, stopLoading } = useLoading();
  const auth = useContext(AuthContext);

  const [templates, setTemplates] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [idTemplate, setIdTemplate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mensagemDeErro, setMensagemDeErro] = useState("");
  const [tipo, setTipo] = useState("geral");
  const [canal, setCanal] = useState("email"); // "email" | "sms"

  /* Os modelos de EMAIL passaram a ser escritos pela HealthTech; aqui só se
     consultam. Os de SMS continuam da clínica.
     A razão é a partilha: todos os emails saem do mesmo domínio, e a reputação
     de envio é comum a todas as clínicas — um texto com ar de publicidade numa
     delas piora a entrega das outras. O SMS não tem essa propriedade.
     O servidor já recusa criar, editar e apagar; isto é para o ecrã não
     oferecer botões que vão dar erro. */
  const podeEditarModelos = canal !== "email";

  // Campos do formulário
  const [nome, setNome] = useState("");
  const [assunto, setAssunto] = useState("");
  const [corpo, setCorpo] = useState("");

  // Ref para o textarea do corpo — para inserir variáveis na posição do cursor
  const corpoRef = useRef(null);

  useEffect(() => {
    setTipo(canal === "sms" ? "lembrete" : "geral");
  }, [canal]);

  // ── Carregar templates ──────────────────────────────────────────────────────
  useEffect(() => {
    const fetchTemplates = async () => {
      startLoading();
      try {
        const [resEmail, resSms] = await Promise.all([
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/emailtemplates/${clinicaId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
          sendRequest(
            `${process.env.REACT_APP_BACKEND_LINK}/api/smstemplates/${clinicaId}`,
            "GET",
            null,
            { Authorization: "Bearer " + auth.token },
          ),
        ]);
        setTemplates([
          ...(resEmail.templates || []),
          ...(resSms.templates || []),
        ]);
      } catch (err) {
        console.error("Erro ao carregar templates:", err);
      } finally {
        stopLoading();
      }
    };
    fetchTemplates();
  }, [clinicaId, auth.token, sendRequest]);

  // ── Reset form ──────────────────────────────────────────────────────────────
  const resetarValores = () => {
    setNome("");
    setAssunto("");
    setCorpo("");
    setMensagemDeErro("");
    setIdTemplate(null);
    setTipo(canal === "sms" ? "lembrete" : "geral");
  };

  const handleModalClose = () => {
    resetarValores();
    setOpenModal(false);
  };

  // ── Inserir variável na posição do cursor no textarea ───────────────────────
  const inserirVariavel = (chave) => {
    const textarea = corpoRef.current?.querySelector("textarea");
    if (!textarea) {
      setCorpo((prev) => prev + `{{${chave}}}`);
      return;
    }

    const inicio = textarea.selectionStart;
    const fim = textarea.selectionEnd;
    const novoCorpo =
      corpo.slice(0, inicio) + `{{${chave}}}` + corpo.slice(fim);
    setCorpo(novoCorpo);

    // Reposicionar cursor após a variável inserida
    setTimeout(() => {
      textarea.focus();
      const novaPosicao = inicio + chave.length + 4; // 4 = {{ + }}
      textarea.setSelectionRange(novaPosicao, novaPosicao);
    }, 0);
  };

  const urlBase =
    canal === "sms"
      ? `${process.env.REACT_APP_BACKEND_LINK}/api/smstemplates`
      : `${process.env.REACT_APP_BACKEND_LINK}/api/emailtemplates`;

  const guardarTemplate = async () => {
    startLoading();
    try {
      const body =
        canal === "sms"
          ? JSON.stringify({ nome, corpo, tipo })
          : JSON.stringify({ nome, assunto, corpo, tipo });

      const response = await sendRequest(
        `${urlBase}/${clinicaId}`,
        "POST",
        body,
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );
      setTemplates((prev) => [response.template, ...prev]);
      handleModalClose();
      toast.success("Template criado com sucesso!");
    } catch (err) {
      console.error("Erro ao guardar template:", err);
      toast.error("Erro ao guardar template.");
    } finally {
      stopLoading();
      setIsSubmitting(false);
    }
  };

  const editarTemplate = async () => {
    startLoading();
    try {
      const body =
        canal === "sms"
          ? JSON.stringify({ nome, corpo, tipo })
          : JSON.stringify({ nome, assunto, corpo, tipo });

      const response = await sendRequest(
        `${urlBase}/${clinicaId}/${idTemplate}`,
        "PATCH",
        body,
        {
          Authorization: "Bearer " + auth.token,
          "Content-Type": "application/json",
        },
      );
      setTemplates((prev) =>
        prev.map((t) =>
          t._id === idTemplate || t.id === idTemplate ? response.template : t,
        ),
      );
      handleModalClose();
      toast.success("Template actualizado com sucesso!");
    } catch (err) {
      console.error("Erro ao editar template:", err);
      toast.error("Erro ao editar template.");
    } finally {
      stopLoading();
      setIsSubmitting(false);
    }
  };

  const preEditarTemplate = (template) => {
    setIdTemplate(template._id || template.id);
    setNome(template.nome || "");
    setAssunto(template.assunto || "");
    setCorpo(template.corpo || "");
    setOpenModal(true);
    setTipo(template.tipo || "geral");
  };

  // ── Apagar ──────────────────────────────────────────────────────────────────
  const apagarTemplate = async (id) => {
    startLoading();
    try {
      await sendRequest(`${urlBase}/${clinicaId}/${id}`, "DELETE", null, {
        Authorization: "Bearer " + auth.token,
      });
      setTemplates((prev) => prev.filter((t) => t._id !== id && t.id !== id));
      toast.success("Template removido com sucesso!");
    } catch (err) {
      console.error("Erro ao apagar template:", err);
      toast.error("Erro ao apagar template.");
    } finally {
      stopLoading();
    }
  };

  // ── Validação e submit ──────────────────────────────────────────────────────
  const beforeSubmit = () => {
    if (!nome.trim()) {
      setMensagemDeErro("O nome do template é obrigatório.");
      return;
    }
    if (canal === "email" && !assunto.trim()) {
      setMensagemDeErro("O assunto é obrigatório.");
      return;
    }
    if (!corpo.trim()) {
      setMensagemDeErro("O corpo do email é obrigatório.");
      return;
    }

    setMensagemDeErro("");
    if (isSubmitting) return;
    setIsSubmitting(true);
    idTemplate ? editarTemplate() : guardarTemplate();
  };

  const templatesFiltrados = templates.filter((t) =>
    canal === "sms"
      ? ["lembrete", "confirmacao"].includes(t.tipo)
      : !["lembrete", "confirmacao"].includes(t.tipo),
  );

  return (
    <>
      <div className="categoriasGeral__container">
        <div
          style={{
            backgroundColor: "#e3f2fd",
            border: "1px solid #90caf9",
            borderRadius: "8px",
            padding: "16px 20px",
            marginBottom: "20px",
            display: "flex",
            gap: "15px",
            alignItems: "flex-start",
            gridColumn: "1/-1",
          }}
        >
          <InfoOutlinedIcon style={{ color: "#1976d2", marginTop: "2px" }} />
          <div
            style={{ fontSize: "14px", color: "#1565c0", lineHeight: "1.6" }}
          >
            <strong
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "15px",
              }}
            >
              Como funcionam os Templates de Comunicação?
            </strong>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              <li>
                São os textos usados nas mensagens automáticas enviadas aos
                pacientes, por <strong>email</strong> e por <strong>SMS</strong>.
                Cada canal tem os seus.
              </li>
              <li>
                As <strong>variáveis</strong> — como o nome do cliente ou a data
                — são substituídas pelos dados reais no momento do envio.
              </li>
              <li>
                Alterar um template só afeta as mensagens enviadas a partir daí;
                as já enviadas ficam como estavam.
              </li>
              <NotaPartilhaUnidades />
            </ul>
          </div>
        </div>

        {/* Separador de canais */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          {["email", "sms"].map((c) => (
            <span
              key={c}
              onClick={() => setCanal(c)}
              style={{
                padding: "6px 18px",
                borderRadius: "20px",
                fontSize: "13px",
                cursor: "pointer",
                fontWeight: canal === c ? "600" : "400",
                background: canal === c ? "#175c93" : "#f0f0f0",
                color: canal === c ? "#fff" : "#555",
                userSelect: "none",
              }}
            >
              {c === "email" ? "Email" : "SMS"}
            </span>
          ))}
        </div>

        {canal === "email" && (
          <div
            style={{
              background: "#f6f8fc",
              border: "1px solid #e3e8f0",
              borderRadius: "6px",
              padding: "12px 16px",
              margin: "8px 0 16px",
              fontSize: "13px",
              color: "#3b4a66",
              lineHeight: 1.5,
            }}
          >
            Os modelos de email são escritos e mantidos pela HealthTech, para
            garantir que as mensagens chegam à caixa de entrada dos pacientes e
            não ao spam — todos os emails saem do mesmo domínio, e o que uma
            clínica envia afeta a entrega das outras.
            <br />
            Precisa de um modelo novo, ou de alterar algum? Fale connosco.
          </div>
        )}

        {podeEditarModelos &&
          (auth.role === "Médico/Administrador" ||
            auth.role === "Gerente") && (
          <div
            className="categoriasGeral__container__btn-adicionar"
            style={{ width: "240px" }}
          >
            <Button
              variant="contained"
              color="success"
              onClick={() => setOpenModal(true)}
            >
              {canal === "sms" ? "Novo Modelo de SMS" : "Novo Modelo de Email"}
            </Button>
          </div>
        )}

        {/* Lista de templates */}
        {templatesFiltrados.map((template) => (
          <div
            className="categoriasFinanceiro__container__categoria"
            key={template._id || template.id}
          >
            <div className="categoriasFinanceiro__container__categoria--1">
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <EmailIcon style={{ color: "#175c93" }} />
                <div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>{template.nome}</span>
                    <span
                      style={{
                        background: canal === "sms" ? "#e8f7e8" : "#e8f1fd",
                        color: canal === "sms" ? "#2e7d32" : "#1976d2",
                        borderRadius: "4px",
                        padding: "1px 8px",
                        fontSize: "11px",
                      }}
                    >
                      {(canal === "sms"
                        ? TIPOS_TEMPLATE_SMS
                        : TIPOS_TEMPLATE_EMAIL
                      ).find((t) => t.value === template.tipo)?.label ||
                        (canal === "sms" ? "Lembrete" : "Geral")}
                    </span>
                  </div>
                  <div style={{ fontSize: "13px", color: "#666" }}>
                    Assunto: {template.assunto}
                  </div>
                  {template.variaveis?.length > 0 && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#999",
                        marginTop: "3px",
                      }}
                    >
                      Variáveis:{" "}
                      {template.variaveis.map((v) => (
                        <span
                          key={v}
                          style={{
                            background: "#e8f1fd",
                            color: "#1976d2",
                            borderRadius: "4px",
                            padding: "1px 6px",
                            marginRight: "4px",
                            fontSize: "11px",
                          }}
                        >
                          {`{{${v}}}`}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="categoriasFinanceiro__container__categoria--2">
              {podeEditarModelos &&
                (auth.role === "Médico/Administrador" ||
                  auth.role === "Gerente") && (
                <Popconfirm
                  title="Remover modelo de email"
                  description="Confirma a remoção deste modelo?"
                  icon={<ErrorIcon style={{ color: "red" }} />}
                  okText="Sim"
                  cancelText="Não"
                  onConfirm={() => apagarTemplate(template._id || template.id)}
                  className="popConfirm--biggerPadding"
                >
                  <span>Remover</span>
                </Popconfirm>
              )}
              {podeEditarModelos &&
                (auth.role === "Médico/Administrador" ||
                  auth.role === "Gerente") && (
                  <span onClick={() => preEditarTemplate(template)}>
                    Editar
                  </span>
                )}
            </div>
          </div>
        ))}

        {templates.length === 0 && (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            {canal === "email"
              ? "Ainda não há modelos de email para esta clínica. Fale connosco para os configurarmos."
              : "Nenhum modelo de SMS criado."}
          </div>
        )}

        {/* Modal criar/editar */}
        <Modal open={openModal} onClose={handleModalClose}>
          <Box
            className="fluxo-atendimento__modal__box adicionar-categoria__modal"
            style={{ maxWidth: "600px" }}
          >
            <h1 className="fluxo-atendimento__modal__titulo">
              {idTemplate ? "Editar Modelo de Email" : "Novo Modelo de Email"}
            </h1>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "20px" }}
            >
              <TextField
                label="Nome do modelo *"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                fullWidth
                helperText="Ex: Lembrete de consulta, Promoção de limpeza"
              />

              {canal === "email" && (
                <TextField
                  label="Assunto do email *"
                  value={assunto}
                  onChange={(e) => setAssunto(e.target.value)}
                  fullWidth
                  helperText="Podes usar variáveis: ex. Olá {{nome_cliente}}"
                />
              )}

              <TextField
                select
                label="Tipo de template *"
                value={tipo}
                onChange={(e) => setTipo(e.target.value)}
                fullWidth
                SelectProps={{ native: true }}
              >
                {(canal === "sms"
                  ? TIPOS_TEMPLATE_SMS
                  : TIPOS_TEMPLATE_EMAIL
                ).map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </TextField>

              {/* Botões de variáveis */}
              <div>
                <div
                  style={{
                    fontSize: "13px",
                    color: "#555",
                    marginBottom: "8px",
                  }}
                >
                  Clica para inserir uma variável no corpo:
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                  {(canal === "sms" ? VARIAVEIS_SMS : VARIAVEIS_EMAIL).map(
                    (v) => (
                      <span
                        key={v.chave}
                        onClick={() => inserirVariavel(v.chave)}
                        style={{
                          background: "#e8f1fd",
                          color: "#1976d2",
                          borderRadius: "4px",
                          padding: "4px 10px",
                          fontSize: "12px",
                          cursor: "pointer",
                          border: "1px solid #90caf9",
                          userSelect: "none",
                        }}
                      >
                        {`{{${v.chave}}}`} — {v.label}
                      </span>
                    ),
                  )}
                </div>
              </div>

              <TextField
                ref={corpoRef}
                label="Corpo do email *"
                value={corpo}
                onChange={(e) => setCorpo(e.target.value)}
                multiline
                rows={8}
                fullWidth
                helperText="Usa os botões acima para inserir variáveis dinâmicas"
              />
            </div>

            {mensagemDeErro && (
              <div className="erroContainer__categoria-caixa">
                {mensagemDeErro}
              </div>
            )}

            <div className="fluxo-atendimento__modal__bottom_btns">
              <span className="cancel-btn" onClick={handleModalClose}>
                Fechar
              </span>
              <span className="blue-button" onClick={beforeSubmit}>
                {isSubmitting ? "Guardando..." : "Guardar"}
              </span>
            </div>
          </Box>
        </Modal>
      </div>
    </>
  );
}

export default ComunicacaoTemplates;
